import { useEffect } from 'react'
import { focusManager } from '@tanstack/react-query'
import { isTauri } from '@/lib/utils/tauri'
import { createLogger } from '@/lib/logger'

const logger = createLogger('hooks/useTauriFocusRefetch')

/**
 * How long the window must sit in the background before a regained-focus event
 * is allowed to trigger React Query's focus refetch. A quick alt-tab (shorter
 * than this) is ignored so we don't sweep-refetch on every trivial focus flip.
 *
 * This is the coarse "should we even bother checking again?" gate. The fine
 * gate is each query's own `staleTime` (30s by default): even when a focus is
 * allowed through, only queries that are actually stale hit the network. The
 * two compose — you refetch on return only after being away this long AND only
 * the data that's gone stale.
 */
const MIN_BACKGROUND_MS = 60_000 // 1 minute

/**
 * Bridges the Tauri desktop window's native focus events into React Query's
 * `focusManager`, so `refetchOnWindowFocus` actually fires on desktop.
 *
 * Why this is needed: React Query's default focus detection listens for the
 * browser `visibilitychange`/`focus` DOM events. In a packaged Tauri webview,
 * OS-level window focus changes don't map cleanly to those events, so returning
 * to the app after a while never triggered a background refresh — data could
 * sit stale until the next navigation. Web browsers are unaffected (this hook
 * no-ops outside Tauri), so their built-in focus handling stays as-is.
 *
 * Throttling: rather than flipping focus state on every OS event, we only mark
 * the app "unfocused" once it has been backgrounded for `MIN_BACKGROUND_MS`.
 * The subsequent return is then a genuine unfocused→focused transition, which
 * is the only thing that makes React Query refetch. Brief blurs never mark us
 * unfocused, so they cost nothing.
 */
export function useTauriFocusRefetch() {
  useEffect(() => {
    if (!isTauri()) return

    let disposed = false
    let unlisten: (() => void) | undefined
    let blurTimer: ReturnType<typeof setTimeout> | undefined
    // Tracks whether we've explicitly told focusManager we're unfocused, so a
    // brief blur (timer cleared before it fires) never triggers a refetch.
    let markedUnfocused = false

    import('@tauri-apps/api/window')
      .then(({ getCurrentWindow }) =>
        getCurrentWindow().onFocusChanged(({ payload: focused }) => {
          if (focused) {
            if (blurTimer) {
              clearTimeout(blurTimer)
              blurTimer = undefined
            }
            // Only a real long-background→focus transition refetches. If we
            // never crossed the threshold, this is a no-op quick alt-tab.
            if (markedUnfocused) {
              markedUnfocused = false
              focusManager.setFocused(true)
            }
          } else {
            // Defer marking unfocused until we've actually been away a while.
            if (blurTimer) clearTimeout(blurTimer)
            blurTimer = setTimeout(() => {
              markedUnfocused = true
              focusManager.setFocused(false)
            }, MIN_BACKGROUND_MS)
          }
        }),
      )
      .then(fn => {
        if (disposed) fn()
        else unlisten = fn
      })
      .catch(err => {
        logger.warn('[useTauriFocusRefetch] Failed to set up focus bridge', err)
      })

    return () => {
      disposed = true
      if (blurTimer) clearTimeout(blurTimer)
      unlisten?.()
      // Hand focus detection back to React Query's default (browser) handling.
      if (markedUnfocused) focusManager.setFocused(undefined)
    }
  }, [])
}
